import React, { useState, useEffect, useRef } from 'react';
import { withTranslation } from '../ChildrenContext.js';
import PropTypes from 'prop-types';
import { Link } from '../RouterCompatibility';
import { I18nFormatter } from '../../utils/I18n/I18n';
import UserInfoActions from '../../actions/UserInfoActions';
import CustomTooltip from '../Tooltip';
import SessionActions from '../../actions/SessionActions';
import Dropdown from '../Dropdown';
import runtimeConfig from '../../config/runtimeConfig';

import Locale from '../Locale';
import UserStore from "../../stores/UserStore";

// Static paths resolved from legacy configRouter (wave 8)
const PAYOUT_PATH = '/office/finances/payout';
const PROFILE_PATH = '/office/settings/profile';

const images = {
    user: require('shared_img/icons/user.svg'),
    profile: require('shared_img/icons/header-menu-user.svg'),
    admin: require('shared_img/icons/header-menu-admin.svg'),
    advertiser: require('shared_img/icons/header-menu-advertiser.svg'),
    exit: require('shared_img/icons/header-menu-exit.svg'),
};

const User = ({ pathname = window.location.pathname, t, ...props }) => {
    const [balanceAmount, setBalanceAmount] = useState(props.balanceAmount);
    const [currency, setCurrency] = useState(props.currency);
    const [name, setName] = useState(props.name);
    const [publisherIsReferral, setPublisherIsReferral] = useState(props.publisherIsReferral);
    const [timezone, setTimezone] = useState(props.timezone);
    const [isAdmin, setIsAdmin] = useState(UserStore.isAdmin());
    const [isAdvertiser, setIsAdvertiser] = useState(UserStore.isAdvertiser());
    const [userId, setUserId] = useState(UserStore.getPublisherID());
    const [impersonating, setImpersonating] = useState(UserStore.getImpersonating());

    const pathnameRef = useRef(pathname);
    const staticNames = (t && t.user) || {};
    const staticNamesMenu = (t && t.userMenu) || {};
    const referralBadges = (t && t.referral) || {};

    useEffect(() => {
        loadInfo();

        if (props.balanceAmount !== balanceAmount ||
            props.currency !== currency ||
            props.name !== name ||
            props.timezone !== timezone) {
            setBalanceAmount(props.balanceAmount);
            setCurrency(props.currency);
            setName(props.name);
            setTimezone(props.timezone);
        }
    }, [props.balanceAmount, props.currency, props.name, props.timezone]);

    const loadInfo = () => {
        const currentPathname = props.location && props.location.pathname;

        if (currentPathname !== pathnameRef.current) {
            UserInfoActions.loadInfo();
            pathnameRef.current = currentPathname;
        }
    };

    const onExit = () => {
        SessionActions.deleteAccessToken();
    };

    const onClickExit = () => {
        onExit();
        onCloseDropdown();

        window.location.href = runtimeConfig.cpaUrl + (runtimeConfig.static && runtimeConfig.static.exit || '');
    };

    const onCloseDropdown = () => {
        document.querySelector('body').click();
    };

    const renderButtonAdmin = () => {
        if (impersonating) {
            return (
                <li>
                    <a
                        href={`${runtimeConfig.adminUrl}/admin/users/edit/${userId}?_switch_user=_exit`}
                        title={`${staticNames.toAdminPanel}`}
                        onClick={onExit}
                    >
                        <i className="icon">
                            <img src={images.admin} alt="{staticNames.toAdminPanel}"/>
                        </i>
                        {staticNames.toAdminPanel}
                    </a>
                </li>
            );
        }

        if (isAdmin) {
            return (
                <li>
                    <a
                        title={staticNames.adminPanel}
                        href={`${runtimeConfig.adminUrl}/admin`}
                        onClick={() => onCloseDropdown()}
                    >
                        <i className="icon">
                            <img src={images.admin} alt="{staticNames.adminPanel}"/>
                        </i>
                        {staticNames.adminPanel}
                    </a>
                </li>
            );
        }

        return null;
    };

    const renderButtonAdvertiser = () => {
        if (impersonating && isAdvertiser) {
            return (
                <li>
                    <a
                        href={`${runtimeConfig.advertiserUrl}`}
                        title={`${staticNames.toAdvertiserPanel}`}
                        onClick={onExit}
                    >
                        <i className="icon">
                            <img src={images.advertiser} alt="{staticNames.toAdvertiserPanel}"/>
                        </i>
                        {staticNames.toAdvertiserPanel}
                    </a>
                </li>
            );
        }

        if (isAdvertiser) {
            return (
                <li>
                    <a
                        title={staticNames.advertiserPanel}
                        href={`${runtimeConfig.advertiserUrl}`}
                        onClick={() => onCloseDropdown()}
                    >
                        <i className="icon">
                            <img src={images.advertiser} alt="{staticNames.advertiserPanel}"/>
                        </i>
                        {staticNames.advertiserPanel}
                    </a>
                </li>
            );
        }

        return null;
    };

    const renderBadge = () => {
        if (publisherIsReferral) {
            return (
                <span className="badge badge-danger ml10" title={referralBadges.publisher}>RP</span>
            )
        }
    };

    const balanceAmountFormatted = parseFloat(balanceAmount).toFixed(2);

    return (
        <div className="top-header__user-wrapp">
            <CustomTooltip theme={'white'} placement={'bottom'} tooltip={(
                <div className="top-header__user-wrapp__hoverText">
                    <Link className="top-header__balance btn" to={PAYOUT_PATH}>
                        <span className="hidden hidden-xs hidden-sm">{staticNames.balance}: </span>
                        <span className="num">{I18nFormatter.currencyBalance(balanceAmountFormatted, currency)}</span>
                    </Link>
                    <p>{staticNames.toPayout}: {I18nFormatter.currency(props.payoutBalance.amount, props.payoutBalance.currency)}</p>
                    <p>{staticNames.hold}: {I18nFormatter.currency(props.holdBalance.amount, props.holdBalance.currency)}</p>
                </div>
            )}>
                <Link className="top-header__balance btn" to={PAYOUT_PATH}>
                    <span className="hidden hidden-xs hidden-sm">{staticNames.balance}: </span>
                    <span className="num">{I18nFormatter.currencyBalance(balanceAmountFormatted, currency)}</span>
                </Link>
            </CustomTooltip>
            <Locale />
            <Dropdown
                mainClass={{'top-header__user': true}}
                header={(
                    <span className="user-header__wrapper">
                        <img className="user-logo" src={images.user} alt=""/>
                        <span className="user-name hidden-xs hidden-sm">{name} {renderBadge()}</span>
                    </span>
                )}
            >
                <ul className="dropdown-menu">
                    <li>
                        <Link
                            to={PROFILE_PATH}
                            onClick={() => onCloseDropdown()}
                        >
                        <i className="icon">
                                <img src={images.profile} alt="{staticNamesMenu.profile}"/>
                            </i>
                            {staticNamesMenu.profile}
                        </Link>
                    </li>
                    <li>
                        <button
                            className="top-header__user__bottom__logOut bold btn-empty"
                            onClick={onClickExit}
                        >
                            <i className="icon">
                                <img src={images.exit} alt="{staticNamesMenu.profile}"/>
                            </i>
                            {staticNamesMenu.exit}
                        </button>
                    </li>
                    {renderButtonAdmin()}
                    {renderButtonAdvertiser()}
                </ul>
            </Dropdown>
        </div>
    )
};

User.propTypes = {
    pathname: PropTypes.string,
    t: PropTypes.object
};

export default withTranslation(User);
